import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { observer } from '@legendapp/state/react';
import { Ionicons } from '@expo/vector-icons';
import { getSupabaseClient } from '../utils/Supa';
import { currentUser$ } from '../utils/Firebase';
import { isVip, isSubscribed } from '../utils/AppState';

interface GalleryImage {
  id: string;
  imageUrl: string;
  content: string;
  created_at: string;
  character_name: string;
}

const GalleryScreen = observer(() => {
  const insets = useSafeAreaInsets();
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchGalleryImages = async () => {
    const user = currentUser$.get();
    if (!user) return;

    const client = getSupabaseClient();

    // Get all sessions for this user
    const { data: sessions, error: sessionError } = await client
      .from('chat_sessions')
      .select('id, character_id, characters(name)')
      .eq('user_id', user.uid)
      .order('created_at', { ascending: false });

    if (sessionError || !sessions?.length) {
      setImages([]);
      setLoading(false);
      return;
    }

    const sessionIds = sessions.map((s: any) => s.id);

    // Get all messages with images from those sessions
    const { data: messages, error: msgError } = await client
      .from('messages')
      .select('*')
      .in('session_id', sessionIds)
      .not('image_url', 'is', null)
      .order('created_at', { ascending: false });

    if (msgError || !messages) {
      setImages([]);
      setLoading(false);
      return;
    }

    const sessionMap = new Map(
      sessions.map((s: any) => [s.id, s.characters?.name || 'AI'])
    );

    const allItems: GalleryImage[] = messages.map((m: any) => ({
      id: m.id,
      imageUrl: m.image_url,
      content: m.content,
      created_at: m.created_at,
      character_name: sessionMap.get(m.session_id) || 'AI',
    }));

    const maxImages = isVip() ? Infinity : isSubscribed() ? 20 : 5;
    const galleryItems = allItems.slice(0, maxImages);

    setImages(galleryItems);
    setLoading(false);
  };

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchGalleryImages();
    setRefreshing(false);
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top + 12 }]}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Gallery</Text>
        <Text style={styles.headerSubtitle}>{images.length} photos generated</Text>
        {!isVip() && !isSubscribed() && images.length >= 5 && (
          <TouchableOpacity
            style={styles.upgradeBanner}
            onPress={() => Alert.alert('Upgrade', 'Upgrade to Premium for more gallery storage!')}
          >
            <Ionicons name="lock-closed" size={14} color="#FFD700" />
            <Text style={styles.upgradeText}>Upgrade for unlimited photos</Text>
          </TouchableOpacity>
        )}
      </View>

      {loading ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>Loading...</Text>
        </View>
      ) : images.length === 0 ? (
        <View style={styles.emptyState}>
          <Ionicons name="images-outline" size={48} color="#6B7280" />
          <Text style={styles.emptyTitle}>No photos yet</Text>
          <Text style={styles.emptyText}>
            Generate photos from any character to see them here
          </Text>
        </View>
      ) : (
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.grid}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#DB47FF" />
          }
        >
          {images.map((img) => (
            <View key={img.id} style={styles.gridItem}>
              <Image source={{ uri: img.imageUrl }} style={styles.image} resizeMode="cover" />
              <View style={styles.overlay}>
                <Text style={styles.characterName}>{img.character_name}</Text>
                <Text style={styles.date}>
                  {new Date(img.created_at).toLocaleDateString()}
                </Text>
              </View>
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#080B1F',
    paddingHorizontal: 12,
  },
  header: {
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#F6F7FF',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#8D93B3',
    marginTop: 4,
  },
  scroll: {
    flex: 1,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    paddingBottom: 20,
    gap: 8,
  },
  gridItem: {
    width: '48%',
    aspectRatio: 0.75,
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
    maxHeight: 280,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  overlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: 10,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  characterName: {
    color: '#F6F7FF',
    fontSize: 13,
    fontWeight: '600',
  },
  date: {
    color: '#C7CBE8',
    fontSize: 11,
    marginTop: 2,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 24,
  },
  emptyTitle: {
    color: '#F6F7FF',
    fontSize: 18,
    fontWeight: '600',
  },
  emptyText: {
    color: '#8D93B3',
    fontSize: 14,
    textAlign: 'center',
  },
  upgradeBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
    backgroundColor: 'rgba(255,215,0,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,215,0,0.4)',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignSelf: 'flex-start',
  },
  upgradeText: {
    color: '#FFD700',
    fontSize: 12,
    fontWeight: '600',
  },
});

export default GalleryScreen;
